<template>
    <div class="pdf-viewer-container">
        <div class="pdf-viewer-container__left">
            <slot name="left-container"></slot>
        </div>
        <div
            class="pdf-viewer-container__middle"
            :style="{ ...marginStyle }"
        >
            <!-- 工具栏 -->
            <pdf-toolbar
                :scale.sync="scale"
                ref="pdf-toolbar"
                :current-page="currentPage"
                :is-grab.sync="isGrab"
                v-bind="{ pageCount, dimensions, isNotH5Mode }"
            />
            <template>
                <slot name="pdf-header"></slot>
            </template>

            <!-- 文档内容 -->
            <pdf-document
                class="pdf-viewer-body"
                ref="pdf-document"
                v-bind="{ pages, pageCount, currentPage, scale, watermarkText, isImage, dimensions, isGrab, dragInfo }"
                v-on="$listeners"
                @scroll-callback="handleScrollCallBack"
            >
                <template v-slot="scope">
                    <slot v-bind="scope"></slot>
                </template>

                <template v-slot:scroll-footer>
                    <slot name="sidebar-append"></slot>
                </template>

                <template #:scroll-footer>
                    <slot name="scroll-footer"></slot>
                </template>
            </pdf-document>
        </div>
        <div
            v-if="isNotH5Mode"
            :class="['pdf-viewer-container__right', { 'is-open': !showTriggerButton }]"
        >
            <!-- 收起侧边栏 -->
            <div
                v-if="showRightThumbnail"
                class="trigger-button transform-center"
                @click="showTriggerButton = !showTriggerButton"
            >
                <i :class="showTriggerButton ? 'el-icon-d-arrow-left':'el-icon-d-arrow-right'"></i>
            </div>

            <slot name="right-container">
                <pdf-document-thumbnail
                    v-if="showRightThumbnail"
                    v-bind="{ documents, currentDocument, imageBaseUrl }" 
                />
            </slot>
        </div>
    </div>
</template>

<script>
import PdfDocument from './pdf-document.vue';
import PdfToolbar from './pdf-toolbar.vue';
import PdfDocumentThumbnail from './pdf-document-thumbnail/index.vue';
import { getPagePositionConfigs, CSS_DIP } from '../utils/base';
import 'qys-pdfjs/web/pdf_viewer.css';
import { ImageManager } from '../model';
import PDFJSLib from './qys-pdfjs';

/** 快速生成数组 */
const pageRange = (start, end) => {
    let last = end;
    last += 1;
    return Array(last - start).fill(0).map((v, i) => i + start);
};


export default {
    components: { PdfDocument, PdfToolbar, PdfDocumentThumbnail },
    name: 'QysPdfViewer',
    props: {
        h5: Boolean,
        showRightThumbnail: {
            type: Boolean,
            default: true,
        },
        url: String, // pdf文档流请求地址
        cMapUrl: { // 字体解析包请求地址
            type: String,
            default: '/qyswebapp/assets/js/cmaps/'
        },
        options: { // pdf基础配置
            type: Object,
            default: null
        },
        documents: { // 合同文档列表
            type: Array,
        },
        dimensions: { // 当前文档规格
            type: Array,
            default: () => {
                return [];
            }
        },
        currentDocument: { // 当前选中文档
            type: Object,
        },
        watermarkText: String, // 水印文字
        imageBaseUrl: {
            type: String,
            default: `/document/image`
        }, // 图片基本请求地址
        pdfRenderMode: { // 渲染模式
            type: String,
            default: 'STREAM'
        }, // pdf渲染模式 STREAM, IMAGE
        visibleToolbar: Boolean, // 显示工具栏
        leftWidth: {
            default: 0
        },
        rightWidth: {
            default: 0
        },
        isOptimum: {
            type: Boolean,
            default: true
        }, // 使用最佳适配
        isAuto: {
            type: Boolean,
            default: true
        }, // 是否自动适配 最佳适配时不生效
        dragInfo: {        //文档拖拽信息
            type: Object,
            default: () => { return {}; }
        },
    },
    data () {
        return {
            pageCount: 0,
            pdfDocument: null,
            pages: [],
            focusPage: 1,
            currentPage: 1,
            scale: 1,
            isGrab: false,
            showTriggerButton: false
        };
    },
    provide () {
        return {
            updateCurrentPage: this.updateCurrentPage,
            getPagePositionConfigs: this.getPagePositionConfigs,
            scrollToPage: this.scrollToPage,
            doChangeDocument: this.$listeners['on-document-changed'],
            onDrop: this.$listeners['on-drop'],
            onDragOver: this.$listeners['on-drag-over'],
            onDragLeave: this.$listeners['on-drag-leave']
        };
    },
    computed: {
        isNotH5Mode () {
            return !this.h5;
        },
        marginStyle () {
            return {
                marginLeft: `${this.leftWidth}px`,
                marginRight: `${this.rightWidth}px`,
            };
        },
        isImage () {
            return this.pdfRenderMode === 'IMAGE';
        },
        /** 获取文档真实高度 */
        pagePositionConfigs () {
            return getPagePositionConfigs(this.pages, this.scale * CSS_DIP);
        },
        // token 秘钥
        zwdt_access_token () {
            return this.$route.query.zwdt_access_token;
        },
    },
    beforeDestroy () {
        this.pages = [];
    },
    watch: {
        'url': {
            handler (nv) {
                nv && this.doInitDocumentPages();
            },
            immediate: true
        },
        scale: {
            handler (nv = 1) {
                this.$emit('scale-change', Number(nv));
            }
        },
        currentPage: {
            handler (nv) {
                this.$emit('current-page-change', Number(nv));
            },
            immediate: true,
        }
    },
    methods: {
        /** 更新页码 */
        updateCurrentPage (pageNo) {
            this.currentPage = pageNo;
        },

        scrollToPage (pageNo) {
            this.updateCurrentPage(pageNo);
            const top = this.pagePositionConfigs.positions?.[pageNo - 1]?.top ?? 0;
            this.updateScrollTop(top);
        },

        updateScrollTop (scrollTop) {
            this.$refs['pdf-document'].updateScrollTop(scrollTop);
        },

        handleScrollCallBack () {
            this.$refs['pdf-toolbar'].visibleScaleOption = false;
            this.$emit('page-change', this.currentPage);
        },

        doInitDocumentPages () {
            const renderFunc = new Map([
                ['STREAM', this.doInitDocumentStream],
                ['IMAGE', this.doInitDocumentImage]
            ]);
            this.pages = [];
            this.currentPage = 1;
            this.pageCount = 0;
            renderFunc.get(this.pdfRenderMode)?.();
            // 切换文档时自动切换到适应页面宽度
            this.$nextTick(() => {
                this.updateScrollTop(0);
                this.$refs['pdf-toolbar'].handleAutoScale('WIDTH', this.isAuto, this.isOptimum);
            });
        },

        /** 获取pdf文档 */
        doInitDocumentStream () {
            try {
                let params = {
                    url: this.url,
                    cMapUrl: this.cMapUrl,
                    cMapPacked: true,
                };
                if (this.options) {
                    params = Object.assign(params, this.options);
                }

                PDFJSLib.getDocument(params).promise.then(async (document) => {
                    this.pdfDocument = document;
                    this.pageCount = document.numPages;
                    this.fetchPages();
                    this.$emit('document-rendered');
                });
            } catch (error) {
                this.$emit('document-render-error', error);
            }
        },

        /** 获取pdf页面数据 */
        async getPages (pdf, start, end) {
            try {
                const requestSet = pageRange(start, end).map(pageNo => pdf.getPage(pageNo));
                return Promise.all(requestSet);
            } catch (error) {
                throw new Error(error);
            }
        },

        /** 加载pdf数据 每次加载10页 PAGE_SIZE */
        async fetchPages () {
            try {
                if (!this.pageCount) return;
                if (this.pageCount && this.pageCount === this.pages.length) return;
                const partsOfPages = await this.getPages(this.pdfDocument, 1, this.pageCount);
                this.pages = partsOfPages;
            } catch (error) {
                console.log(error);
            }
        },

        /** 获取合同图片 */
        doInitDocumentImage () {
            try {
                if (!this.dimensions.length) throw new Error('未设置文档信息： dimensions');
                if (!this.imageBaseUrl) throw new Error('未设置图片合同请求地址：imageBaseUrl');

                this.dimensions.forEach((dimension, index) => {
                    const pageNumber = index + 1;
                    const url = `${this.BASE_APP_PATH}${this.imageBaseUrl}?zwdt_access_token=${this.zwdt_access_token}&documentId=${this.currentDocument.id}&pageNo=${pageNumber}&_t=${Date.now()}`;
                    const img = new ImageManager(dimension, pageNumber, url);
                    this.pages.push(img);
                });
                this.pageCount = this.dimensions.length;
                this.$emit('document-rendered');
            } catch (error) {
                this.$emit('document-render-error', error);
            }
        },
    },
};
</script>


<style lang="less" scoped>
.pdf-viewer-container {
    height: 100%;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;
    position: relative;

    * {
        box-sizing: border-box;
    }

    .pdf-viewer-container__left {
        position: absolute;
        top: 0;
        left: 0;
        width: auto;
        height: 100%;
        z-index: 0;
        overflow: auto;
        transition: all 0.5s ease-in-out;
    }

    .pdf-viewer-container__right {
        position: absolute;
        top: 0;
        right: 8px;
        width: auto;
        height: 100%;
        z-index: 0;
        width: 0;
        transition: all 0.5s ease-in-out;

        &.is-open {
            width: auto;
        }

        .trigger-button {
            width: 16px;
            height: 64px;
            background: @pdf-header;
            color: @grey;
            position: absolute;
            z-index: 1000;
            top: 50%;
            left: 0;
            cursor: pointer;
            transform: translate(-100%, -50%);
            border-radius: 20px 0 0 20px;
            transition: all 0.3s;

            &:hover {
                width: 24px;
            }
        }
        .close .trigger-button {
            right: 23px;
        }
    }

    .pdf-viewer-container__middle {
        display: block;
        height: 100%;
        text-align: center;
        overflow: visible;
        z-index: 1;
    }
}
</style>
<style>
body,
html {
    margin: 0;
}
</style>
